import { createAction, props } from '@ngrx/store';

import { AdditionalInvoiceDataRow } from '../../components/final-setup/models/final-setup.model';
import {
  AnimalGroup,
  BalancePerDeliveryLine,
  CarbonFootprintResponse,
  Concept,
  Costs,
  Cycle,
  DayOldChick,
  Delivery,
  Departure,
  Discount,
  FarmResource,
  FarmResourcesDivision,
  Hatchery,
  Product,
  RouteTransitionBalance,
  Slaughterhouse,
} from '../../models/cycle.model';
import {
  SlaughterInvoice,
  SlaughterReport,
  SlaughterReportBarnLinksRequest,
  UpsertBalances,
} from '../../models/flock-registration-service.model';

export const loadData = createAction('[Cycle] Load data');
export const loadCyclesRequest = createAction('[Cycle] Load cycles request');
export const loadCyclesSuccess = createAction(
  '[Cycle] Load cycles success',
  props<{ cycles: Cycle[] }>(),
);
export const loadCyclesFailed = createAction(
  '[Cycle] Load cycles failed',
  props<{ error: Error }>(),
);

export const upsertCycleRequest = createAction(
  '[Cycle] Upsert cycle request',
  props<{ cycle: Cycle }>(),
);
export const upsertCycleSuccess = createAction(
  '[Cycle] Upsert cycle success',
  props<{ cycle: Cycle }>(),
);
export const upsertCycleFailed = createAction(
  '[Cycle] Upsert cycle failed',
  props<{ error: Error }>(),
);
export const upsertBalancesRequest = createAction(
  '[Cycle] Upsert balances request',
  props<{ upsertBalances: UpsertBalances }>(),
);
export const upsertBalancesSuccess = createAction(
  '[Cycle] Upsert balances success',
  props<{ upsertBalances: UpsertBalances }>(),
);
export const upsertBalancesFailed = createAction(
  '[Cycle] Upsert balances failed',
  props<{ error: Error }>(),
);

export const deleteCycleRequest = createAction(
  '[Cycle] Delete cycle request',
  props<{ cycle: Cycle }>(),
);
export const deleteCycleWithConfirmation = createAction(
  '[Cycle] Confirm Delete cycle',
  props<{ cycle: Cycle }>(),
);
export const deleteCycleSuccess = createAction(
  '[Cycle] Delete cycle success',
  props<{ cycle: Cycle }>(),
);
export const deleteCycleFailed = createAction(
  '[Cycle] Delete cycle failed',
  props<{ error: Error }>(),
);

export const loadConceptsRequest = createAction('[Cycle] Load concepts request');
export const loadConceptsSuccess = createAction(
  '[Cycle] Load concepts success',
  props<{ concepts: Concept[] }>(),
);
export const loadConceptsFailed = createAction(
  '[Cycle] Load concepts failed',
  props<{ error: Error }>(),
);

export const loadHatcheriesRequest = createAction('[Cycle] Load hatcheries request');
export const loadHatcheriesSuccess = createAction(
  '[Cycle] Load hatcheries success',
  props<{ hatcheries: Hatchery[] }>(),
);
export const loadHatcheriesFailed = createAction(
  '[Cycle] Load hatcheries failed',
  props<{ error: Error }>(),
);

export const loadProductsRequest = createAction(
  '[Cycle] Load products request',
  props<{ cycleId: number; previousCycleCount: number }>(),
);
export const loadProductsSuccess = createAction(
  '[Cycle] Load products success',
  props<{ products: Product[] }>(),
);
export const loadProductsFailed = createAction(
  '[Cycle] Load products failed',
  props<{ error: Error }>(),
);

export const loadSlaughterhousesRequest = createAction('[Cycle] Load slaughterhouses request');
export const loadSlaughterhousesSuccess = createAction(
  '[Cycle] Load slaughterhouses success',
  props<{ slaughterhouses: Slaughterhouse[] }>(),
);
export const loadSlaughterhousesFailed = createAction(
  '[Cycle] Load slaughterhouses failed',
  props<{ error: Error }>(),
);

export const createCycle = createAction('[Cycle] Create cycle', props<{ cycle: Cycle }>());

export const upsertCycleGeneral = createAction(
  '[Cycle] Upsert cycle general',
  props<{ cycle: Cycle }>(),
);

export const editDayOldChick = createAction(
  '[Cycle] Edit day old chick',
  props<{ cycleId: number; dayOldChick: DayOldChick }>(),
);
export const addDayOldChick = createAction(
  '[Cycle] Add day old chick',
  props<{ cycleId: number; dayOldChick: DayOldChick }>(),
);
export const deleteDayOldChick = createAction(
  '[Cycle] delete day old chick',
  props<{ cycleId: number; id: number }>(),
);
export const deleteDayOldChickWithConfirmation = createAction(
  '[Cycle] confirm delete day old chick',
  props<{ cycleId: number; id: number; isUsedInBalance: boolean }>(),
);

export const editDeparture = createAction(
  '[Cycle] Edit departure',
  props<{ cycleId: number; departure: Departure }>(),
);
export const addDeparture = createAction(
  '[Cycle] Add departure',
  props<{ cycleId: number; departure: Departure }>(),
);
export const deleteDeparture = createAction(
  '[Cycle] delete departure',
  props<{ cycleId: number; id: number }>(),
);
export const deleteDepartureWithConfirmation = createAction(
  '[Cycle] confirm delete departure',
  props<{ cycleId: number; id: number }>(),
);

export const importManualDeliveriesCsvRequest = createAction(
  '[Cycle] Import manual deliveries CSV request',
  props<{ cycleId: number; file: File }>(),
);
export const importManualDeliveriesCsvSuccess = createAction(
  '[Cycle] Import manual deliveries CSV success',
);
export const importManualDeliveriesCsvFailed = createAction(
  '[Cycle] Import manual deliveries CSV failed',
  props<{ error: Error }>(),
);

export const importDeparturesCsvRequest = createAction(
  '[Cycle] Import departures CSV request',
  props<{ cycleId: number; file: File }>(),
);
export const importDeparturesCsvSuccess = createAction('[Cycle] Import departures CSV success');
export const importDeparturesCsvFailed = createAction(
  '[Cycle] Import departures CSV failed',
  props<{ error: Error }>(),
);

export const calculateBalanceRequest = createAction('[Cycle] Calculate Balance Request');
export const calculateBalanceSuccess = createAction(
  '[Cycle] Calculate Balance success',
  props<{ cycle: Cycle }>(),
);
export const calculateBalanceError = createAction(
  '[Cycle] Calculate Balance error',
  props<{ error: Error }>(),
);

export const editBalanceRow = createAction(
  '[Cycle] Edit balance row',
  props<{ cycleId: number; balanceRow: BalancePerDeliveryLine }>(),
);
export const addBalanceRow = createAction(
  '[Cycle] Add balance row',
  props<{ cycleId: number; balanceRow: BalancePerDeliveryLine }>(),
);
export const deleteBalanceRow = createAction(
  '[Cycle] delete balance row',
  props<{ cycleId: number; id: string }>(),
);
export const deleteBalanceRowWithConfirmation = createAction(
  '[Cycle] confirm delete balance row',
  props<{ cycleId: number; id: string }>(),
);

export const upsertCosts = createAction(
  '[Cycle] Upsert cycle costs',
  props<{ cycleId: number; costs: Costs }>(),
);

export const setSelectedProduct = createAction(
  '[Cycle] Set Selected product',
  props<{ product?: Product }>(),
);

export const setIsCycleDetailedOverview = createAction(
  '[Cycle] Set Cycle Detailed Overview Switch',
  props<{ value: boolean }>(),
);

export const setSelectedCycle = createAction(
  '[Cycle] Set Selected Cycle',
  props<{ cycleId?: number }>(),
);

export const clearCycleState = createAction('[Cycle] Clears the cycle state');

export const loadDeliveriesRequest = createAction('[Cycle] Load deliveries request');

export const loadDeliveriesSuccess = createAction(
  '[Cycle] Load deliveries success',
  props<{ deliveries: Delivery[] }>(),
);

export const loadDeliveriesFailed = createAction(
  '[Cycle] Load deliveries failed',
  props<{ error: Error }>(),
);

export const setRouteTransitionBalance = createAction(
  '[Cycle] Set route transition balance',
  props<{ routeTransitionBalance: RouteTransitionBalance }>(),
);

export const setBalancePendingChanges = createAction('[Cycle] Set balance to pending changes');

export const loadSlaughterReportsRequest = createAction(
  '[Cycle] Load SlaughterReports request',
  props<{ fromDate: string }>(),
);

export const loadSlaughterReportsSuccess = createAction(
  '[Cycle] Load SlaughterReports success',
  props<{ slaughterReports: SlaughterReport[] }>(),
);

export const loadSlaughterReportsFailed = createAction(
  '[Cycle] Load SlaughterReports failed',
  props<{ error: Error }>(),
);

export const linkSlaughterReportsRequest = createAction(
  '[Cycle] Link SlaughterReports request',
  props<{ cycleId: number; links: SlaughterReportBarnLinksRequest }>(),
);

export const linkSlaughterReportsFailed = createAction(
  '[Cycle] Link SlaughterReports failed',
  props<{ error: Error }>(),
);

export const setSelectedDiscountId = createAction(
  '[Cycle] Set selected discount id',
  props<{ id?: number }>(),
);

export const editDiscount = createAction(
  '[Cycle] Edit discount',
  props<{ cycleId: number; discount: Discount }>(),
);

export const addDiscount = createAction(
  '[Cycle] Add discount',
  props<{ cycleId: number; discount: Discount }>(),
);

export const addDiscounts = createAction(
  '[Cycle] Add discounts',
  props<{ cycleId: number; discounts: Discount[] }>(),
);

export const deleteDiscount = createAction(
  '[Cycle] Delete discount',
  props<{ cycleId: number; id: number }>(),
);

export const deleteDiscountWithConfirmation = createAction(
  '[Cycle] Confirm delete discount',
  props<{ cycleId: number; id: number }>(),
);

export const upsertAnimalGroup = createAction(
  '[Cycle] Upsert AnimalGroup',
  props<{ cycleId: number; animalGroup: AnimalGroup }>(),
);

export const loadSlaughterInvoicesRequest = createAction(
  '[Cycle] Load SlaughterInvoices request',
  props<{ fromDate: string; linkedCycleId?: number }>(),
);

export const loadSlaughterInvoicesSuccess = createAction(
  '[Cycle] Load SlaughterInvoices success',
  props<{ isLinked: boolean; slaughterInvoices: SlaughterInvoice[] }>(),
);

export const linkSlaughterInvoices = createAction(
  '[Cycle] Link SlaughterInvoices to cycle',
  props<{ ids: number[]; cycleId: number }>(),
);

export const linkSlaughterInvoicesSuccess = createAction(
  '[Cycle] Link SlaughterInvoices to cycle success',
);

export const editAdditionalInvoice = createAction(
  '[Cycle] Edit additional invoice',
  props<{ cycleId: number; invoice: AdditionalInvoiceDataRow }>(),
);

export const addAdditionalInvoice = createAction(
  '[Cycle] Add additional invoice',
  props<{ cycleId: number; discount: Discount }>(),
);

export const deleteAdditionalInvoice = createAction(
  '[Cycle] Delete additional invoice',
  props<{ cycleId: number; id: number }>(),
);

export const addAutomaticDiscounts = createAction(
  '[Cycle] Add automatic discounts',
  props<{ isFinalBalance: boolean }>(),
);

export const addAutomaticDiscountsSkipped = createAction('[Cycle] Add automatic discounts skipped');

export const loadFarmResourcesRequest = createAction('[Cycle] Load Farm-Resources request');

export const loadFarmResourcesSuccess = createAction(
  '[Cycle] Load Farm-Resources success',
  props<{ farmResources: FarmResource[]; cycleResources: FarmResourcesDivision[] }>(),
);

export const loadFarmResourcesError = createAction(
  '[Cycle] Load Farm-Resources Error',
  props<{ error: Error }>(),
);

export const upsertFarmResourceRequest = createAction(
  '[Cycle] Upsert Farm-Resource request',
  props<{ request: FarmResource }>(),
);

export const upsertFarmResourceSuccess = createAction(
  '[Cycle] Upsert Farm-Resource success',
  props<{ farmResource: FarmResource }>(),
);

export const upsertFarmResourceError = createAction(
  '[Cycle] Upsert Farm-Resource Error',
  props<{ error: Error }>(),
);

export const deleteFarmResourceWithConfirmationRequest = createAction(
  '[Cycle] Delete Farm-Resource request with confirmation',
  props<{ id: number }>(),
);

export const deleteFarmResourceRequest = createAction(
  '[Cycle] Delete Farm-Resource request',
  props<{ id: number }>(),
);

export const deleteFarmResourceSuccess = createAction(
  '[Cycle] Delete Farm-Resource success',
  props<{ id: number }>(),
);

export const deleteFarmResourceError = createAction(
  '[Cycle] Delete Farm-Resource Error',
  props<{ error: Error }>(),
);

export const loadCarbonFootprintRequest = createAction(
  '[Cycle] Load carbon footprint request',
  props<{ cycleId: number }>(),
);
export const loadCarbonFootprintSuccess = createAction(
  '[Cycle] Load carbon footprint Success',
  props<{ data: CarbonFootprintResponse }>(),
);
export const loadCarbonFootprintError = createAction(
  '[Cycle] Load carbon footprint Error',
  props<{ error: Error }>(),
);

export const calculateCarbonFootprintRequest = createAction(
  '[Cycle] Calculate carbon footprint Request',
  props<{ cycleId: number }>(),
);
export const calculateCarbonFootprintStart = createAction(
  '[Cycle] Calculate carbon footprint Start',
);
export const calculateCarbonFootprintFailure = createAction(
  '[Cycle] Calculate carbon footprint Failure',
  props<{ error: string }>(),
);

export const refreshCarbonFootprintRequest = createAction(
  '[Cycle] Refresh carbon footprint request',
  props<{ cycleId: number }>(),
);
export const refreshCarbonFootprintFailure = createAction(
  '[Cycle] Refresh carbon footprint Error',
  props<{ error: Error }>(),
);

export const resetCarbonFootprint = createAction('[Cycle] Reset Carbon Footprint');

export const setSustainabilityNoticeVisibility = createAction(
  '[Sustainability] Set sustainability notice visibility',
  props<{ visible: boolean }>(),
);
